import * as Sentry from '@sentry/react'
import Axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios'
import {
  setupCache,
  CacheProperties,
  AxiosCacheInstance,
  buildMemoryStorage,
  CacheRequestConfig,
} from 'axios-cache-interceptor'
import { merge } from 'es-toolkit'
import { getErrorStatus, showLogoutMessage } from 'utils/errorsUtil'

import { HTTP_STATUS, REDIRECT_URLS } from '../config'

// sigleton
const COMMON_CACHE_STORAGE = buildMemoryStorage()

export function createAxios(
  createConfig: MyAxiosRequestConfig,
): AxiosCacheInstance {
  const orgAxios = Axios.create(
    merge(
      {
        headers: { 'Content-Type': 'application/json' },
      },
      createConfig,
    ),
  )
  const isTest = process.env.NODE_ENV === 'test'
  let cacheOptions = {}
  if (!isTest) {
    cacheOptions = {
      storage: COMMON_CACHE_STORAGE, // shared by multiple axioses
      debug: console.log,
    }
  }
  const axios = setupCache(orgAxios, cacheOptions)

  let alertAlready = false // to avoid multiple alert
  let loggedIn = false // must be logged in before alert message
  axios.interceptors.response.use(
    // all nomarl response means login is successful
    (response) => {
      loggedIn = true
      return response
    },
    (error: AxiosError) => {
      Sentry.captureException(error)
      if (error.config?.isCallback) {
        window.location.href = REDIRECT_URLS.LOGIN_FAILURE
        return Promise.reject(error)
      }
      const status = getErrorStatus(error)
      if (status === HTTP_STATUS.UNAUTHORIZED) {
        if (alertAlready || loggedIn) {
          showLogoutMessage()
        }
        alertAlready = true
        window.location.href = REDIRECT_URLS.LOGIN
      }
      if (status === HTTP_STATUS.SERVICE_UNAVAILABLE) {
        window.location.href = REDIRECT_URLS.HOME // reload to show maintenance page
      }
      throw error
    },
  )
  return axios
}

type ID2Invalidate = string | string[]
type MyAxiosRequestConfig = AxiosRequestConfig & {
  cache?: boolean | Partial<CacheProperties>
} & { id?: string }

export function createFetcher(createConfig: MyAxiosRequestConfig) {
  const axios = createAxios(createConfig)

  function removeCache(id2Invalidate?: ID2Invalidate) {
    const myAxios = axios
    if (Array.isArray(id2Invalidate)) {
      id2Invalidate.forEach((id) => myAxios.storage.remove(id))
    } else if (id2Invalidate) {
      myAxios.storage.remove(id2Invalidate)
    }
  }

  function cacheDelOption(id2Invalidate?: ID2Invalidate) {
    if (Array.isArray(id2Invalidate)) {
      return id2Invalidate.reduce(
        (accumulate, current) => ((accumulate[current] = 'delete'), accumulate),
        {},
      )
    } else if (id2Invalidate) {
      return { [id2Invalidate]: 'delete' }
    }
    return undefined
  }

  function getProxy<T>(path: string, options?: MyAxiosRequestConfig) {
    return axios.get<T>(path, { cache: false, ...options } as any)
  }

  async function get(
    path: string,
    options?: MyAxiosRequestConfig,
  ): Promise<unknown> {
    const response = await getProxy(path, options)
    const { data } = response
    return data
  }

  async function getObj<T>(
    path: string,
    options?: MyAxiosRequestConfig,
  ): Promise<T> {
    const response = await getProxy<{ results: T }>(path, options)
    return response.data.results
  }

  async function getObj2<T>(
    path: string,
    options?: MyAxiosRequestConfig,
  ): Promise<T> {
    const response = await getProxy<T>(path, options)
    return response.data
  }

  async function postInvalidate(
    path: string,
    id2Invalidate: ID2Invalidate,
    payload?: Data,
  ): Promise<AxiosResponse<any>> {
    const res = axios.post(path, payload, {
      cache: { update: cacheDelOption(id2Invalidate) },
    })
    return res
  }

  // TODO: refactor to remove any
  async function post<T = any>(
    path: string,
    payload?: Data,
    isBlobResponse?: boolean,
  ) {
    const config: CacheRequestConfig = isBlobResponse
      ? { responseType: 'arraybuffer' }
      : {}
    return axios.post<T>(path, payload, config)
  }

  function postMultipart(
    path: string,
    payload: FormData,
  ): Promise<AxiosResponse<any>> {
    return axios.post(path, payload, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  }

  async function put(
    path: string,
    payload: Data,
    id2Invalidate?: ID2Invalidate,
  ): Promise<AxiosResponse<any>> {
    const ret = await axios.put(path, payload)
    removeCache(id2Invalidate)
    return ret
  }

  async function put2(
    path: string,
    payload: unknown,
  ): Promise<AxiosResponse<any>> {
    return axios.put(path, payload)
  }

  async function sendDelete(
    url: string,
    data?: Data,
    id2Invalidate?: ID2Invalidate,
  ): Promise<AxiosResponse<any>> {
    const ret = await axios.request({
      url,
      data,
      method: 'DELETE',
    })
    removeCache(id2Invalidate)
    return ret
  }

  return {
    get,
    getObj,
    post,
    postInvalidate,
    postMultipart,
    put,
    sendDelete,
    getObj2,
    put2,
  }
}

export type Data = Record<string, unknown>

// function reLoginWhenDev() {
//   // Keep this to make webpack remove content
//   if (process.env.NODE_ENV !== 'development') {
//     return
//   }

//   const user = window.prompt('Logged out. Re-Login again?', 'user2')
//   if (user) {
//     const axios = Axios.create()
//     axios.get(`/?sessionId=${user}`)
//   }
// }
